import { useState, useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function DigitalTransformationPage() {
  const [activeTab, setActiveTab] = useState('all');
  const [messageLength, setMessageLength] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'success' | 'error'>('success');

  useEffect(() => {
    const messageTextarea = document.getElementById('message') as HTMLTextAreaElement;
    if (messageTextarea) {
      messageTextarea.addEventListener('input', (e) => {
        const target = e.target as HTMLTextAreaElement;
        setMessageLength(target.value.length);
      });
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Get checkbox values
    const services: string[] = [];
    formData.getAll('services').forEach((value) => {
      if (value) services.push(value.toString());
    });

    // Create URL-encoded data
    const urlEncodedData = new URLSearchParams();
    urlEncodedData.append('name', formData.get('name') as string);
    urlEncodedData.append('company', formData.get('company') as string);
    urlEncodedData.append('email', formData.get('email') as string);
    urlEncodedData.append('phone', formData.get('phone') as string);
    urlEncodedData.append('services', services.join(', '));
    urlEncodedData.append('message', formData.get('message') as string);
    urlEncodedData.append('privacy', formData.get('privacy') ? 'Agreed' : 'Not Agreed');

    try {
      const response = await fetch('https://readdy.ai/api/form/d50rno5mdo3ncn62h8p0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        setFeedbackType('success');
        form.reset();
        setMessageLength(0);
      } else {
        setFeedbackType('error');
      }
    } catch (error) {
      setFeedbackType('error');
    } finally {
      setIsSubmitting(false);
      setShowFeedback(true);
    }
  };

  const closeFeedback = () => {
    setShowFeedback(false);
  };

  return (
    <div className="bg-white min-h-screen">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-3 border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex items-center text-sm">
            <a href="/" className="text-gray-500 hover:text-teal-600">Home</a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400 mx-1">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <a href="#" className="text-gray-500 hover:text-teal-600">IT Solutions</a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400 mx-1">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <span className="text-teal-600 font-medium">Digital Transformation</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative w-full py-20 bg-gradient-to-r from-teal-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="w-full max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Digital Transformation Solutions
            </h1>
            <p className="text-lg text-gray-700 mb-8">
              Accelerate your business growth with our comprehensive digital transformation services. We help organizations reimagine their business models, optimize processes, and create exceptional digital experiences.
            </p>
            <div className="flex flex-wrap gap-4">
              <button className="bg-teal-600 text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium hover:bg-teal-700 transition-colors cursor-pointer">
                Start Your Transformation
              </button>
              <button className="bg-white border border-gray-300 text-gray-800 px-6 py-3 rounded-lg whitespace-nowrap font-medium hover:bg-gray-50 transition-colors cursor-pointer">
                Schedule Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="w-full lg:w-1/2">
              <span className="text-teal-600 font-semibold mb-2 block text-sm">DIGITAL TRANSFORMATION OVERVIEW</span>
              <h2 className="text-3xl font-bold mb-6">Reimagine Your Business for the Digital Age</h2>
              <p className="text-gray-600 mb-6">
                Digital transformation is more than just implementing new technologies—it's about fundamentally changing how your business operates and delivers value to customers. At Thyamis, we guide organizations through this complex journey with a strategic, people-centered approach.
              </p>
              <p className="text-gray-600 mb-6">
                Our comprehensive digital transformation services help businesses adapt to rapidly changing market conditions, improve operational efficiency, and create new opportunities for growth and innovation.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-3 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-teal-600">
                      <i className="ri-line-chart-line"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">68%</h3>
                    <p className="text-sm text-gray-600">Increase in operational efficiency</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-3 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-teal-600">
                      <i className="ri-customer-service-2-line"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">42%</h3>
                    <p className="text-sm text-gray-600">Improvement in customer satisfaction</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-3 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-teal-600">
                      <i className="ri-time-line"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">35%</h3>
                    <p className="text-sm text-gray-600">Reduction in time-to-market</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-3 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-teal-600">
                      <i className="ri-funds-box-line"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">29%</h3>
                    <p className="text-sm text-gray-600">Average cost reduction</p>
                  </div>
                </div>
              </div>
              <a href="/" className="text-teal-600 font-medium flex items-center hover:text-teal-700 cursor-pointer">
                Return to Main Site
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>
            <div className="w-full lg:w-1/2">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img 
                  src="https://public.readdy.ai/ai/img_res/6589ff1a492c4f3f3f7e8e8a0ee8f460.jpg"
                  alt="Digital Transformation Analytics"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Digital Transformation Services</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive solutions to drive your organization's digital evolution across every aspect of your business.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Service 1 */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-teal-600">
                  <i className="ri-settings-line text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Business Process Automation</h3>
              <p className="text-gray-600 mb-4">
                Streamline operations by automating repetitive tasks and workflows to increase efficiency and reduce errors.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Workflow automation</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Document management</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Process optimization</span>
                </li>
              </ul>
              <a href="#" className="text-teal-600 font-medium flex items-center hover:text-teal-700 cursor-pointer">
                Learn More
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>

            {/* Service 2 */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-teal-600">
                  <i className="ri-cloud-line text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Cloud Migration</h3>
              <p className="text-gray-600 mb-4">
                Seamlessly transition your infrastructure, applications, and data to secure, scalable cloud environments.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Cloud readiness assessment</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Migration planning & execution</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Hybrid cloud solutions</span>
                </li>
              </ul>
              <a href="#" className="text-teal-600 font-medium flex items-center hover:text-teal-700 cursor-pointer">
                Learn More
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>

            {/* Service 3 */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-teal-600">
                  <i className="ri-bar-chart-2-line text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Data Analytics & BI</h3>
              <p className="text-gray-600 mb-4">
                Transform raw data into actionable insights with advanced analytics and business intelligence solutions.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Data strategy development</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">BI implementation</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Predictive analytics</span>
                </li>
              </ul>
              <a href="#" className="text-teal-600 font-medium flex items-center hover:text-teal-700 cursor-pointer">
                Learn More
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>

            {/* Service 4 */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-teal-600">
                  <i className="ri-computer-line text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Digital Workplace Solutions</h3>
              <p className="text-gray-600 mb-4">
                Create a modern, collaborative work environment that enhances productivity and employee experience.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Collaboration platforms</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Remote work enablement</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Digital employee experience</span>
                </li>
              </ul>
              <a href="#" className="text-teal-600 font-medium flex items-center hover:text-teal-700 cursor-pointer">
                Learn More
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>

            {/* Service 5 */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-teal-600">
                  <i className="ri-device-line text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">IoT Integration</h3>
              <p className="text-gray-600 mb-4">
                Connect devices, systems, and applications to create intelligent, data-driven operations and experiences.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">IoT strategy & architecture</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Device management</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Real-time monitoring</span>
                </li>
              </ul>
              <a href="#" className="text-teal-600 font-medium flex items-center hover:text-teal-700 cursor-pointer">
                Learn More
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>

            {/* Service 6 */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-teal-600">
                  <i className="ri-brain-line text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">AI & Machine Learning</h3>
              <p className="text-gray-600 mb-4">
                Leverage artificial intelligence and machine learning to automate complex tasks and gain predictive insights.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">AI solution development</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">ML model training</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Intelligent automation</span>
                </li>
              </ul>
              <a href="#" className="text-teal-600 font-medium flex items-center hover:text-teal-700 cursor-pointer">
                Learn More
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Transformation Approach */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Transformation Approach</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              A proven methodology that ensures successful digital transformation with measurable business outcomes.
            </p>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gray-200 z-0"></div>
            <div className="relative z-10">
              {/* Phase 1 */}
              <div className="flex flex-col md:flex-row items-center mb-16">
                <div className="md:w-1/2 md:pr-12 mb-8 md:mb-0 md:text-right">
                  <span className="text-teal-600 font-semibold mb-2 block text-sm">PHASE 1</span>
                  <h3 className="text-2xl font-bold mb-3">Assessment & Strategy</h3>
                  <p className="text-gray-600">
                    We begin by thoroughly assessing your current digital maturity, business goals, and market position. Our experts work with your team to develop a comprehensive transformation strategy aligned with your objectives.
                  </p>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center md:justify-end">
                      <div className="w-5 h-5 flex items-center justify-center text-teal-600 mr-2 md:order-last md:ml-2 md:mr-0">
                        <i className="ri-check-line"></i>
                      </div>
                      <span className="text-gray-700">Digital maturity assessment</span>
                    </div>
                    <div className="flex items-center md:justify-end">
                      <div className="w-5 h-5 flex items-center justify-center text-teal-600 mr-2 md:order-last md:ml-2 md:mr-0">
                        <i className="ri-check-line"></i>
                      </div>
                      <span className="text-gray-700">Stakeholder workshops</span>
                    </div>
                    <div className="flex items-center md:justify-end">
                      <div className="w-5 h-5 flex items-center justify-center text-teal-600 mr-2 md:order-last md:ml-2 md:mr-0">
                        <i className="ri-check-line"></i>
                      </div>
                      <span className="text-gray-700">Roadmap development</span>
                    </div>
                  </div>
                </div>
                <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center text-white text-xl font-bold z-10 mx-8">
                  1
                </div>
                <div className="md:w-1/2 md:pl-12">
                  <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                      <div className="w-7 h-7 flex items-center justify-center text-teal-600">
                        <i className="ri-search-line text-xl"></i>
                      </div>
                    </div>
                    <h4 className="font-semibold mb-2">Key Deliverables</h4>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Comprehensive digital maturity report</li>
                      <li>• Transformation opportunity analysis</li>
                      <li>• Strategic roadmap with priorities</li>
                      <li>• Business case with ROI projections</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Phase 2 */}
              <div className="flex flex-col md:flex-row items-center mb-16">
                <div className="md:w-1/2 md:pr-12 mb-8 md:mb-0 order-last md:order-first">
                  <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                      <div className="w-7 h-7 flex items-center justify-center text-teal-600">
                        <i className="ri-layout-2-line text-xl"></i>
                      </div>
                    </div>
                    <h4 className="font-semibold mb-2">Key Deliverables</h4>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Solution architecture documents</li>
                      <li>• Technology selection recommendations</li>
                      <li>• Implementation plans</li>
                      <li>• Change management strategy</li>
                    </ul>
                  </div>
                </div>
                <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center text-white text-xl font-bold z-10 mx-8">
                  2
                </div>
                <div className="md:w-1/2 md:pl-12 md:text-left">
                  <span className="text-teal-600 font-semibold mb-2 block text-sm">PHASE 2</span>
                  <h3 className="text-2xl font-bold mb-3">Design & Planning</h3>
                  <p className="text-gray-600">
                    We design the technical architecture and solutions required to achieve your transformation goals. This phase includes selecting the right technologies, planning implementation phases, and preparing for organizational change.
                  </p>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center">
                      <div className="w-5 h-5 flex items-center justify-center text-teal-600 mr-2">
                        <i className="ri-check-line"></i>
                      </div>
                      <span className="text-gray-700">Solution architecture design</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-5 h-5 flex items-center justify-center text-teal-600 mr-2">
                        <i className="ri-check-line"></i>
                      </div>
                      <span className="text-gray-700">Technology selection</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-5 h-5 flex items-center justify-center text-teal-600 mr-2">
                        <i className="ri-check-line"></i>
                      </div>
                      <span className="text-gray-700">Change management planning</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Phase 3 */}
              <div className="flex flex-col md:flex-row items-center mb-16">
                <div className="md:w-1/2 md:pr-12 mb-8 md:mb-0 md:text-right">
                  <span className="text-teal-600 font-semibold mb-2 block text-sm">PHASE 3</span>
                  <h3 className="text-2xl font-bold mb-3">Implementation & Integration</h3>
                  <p className="text-gray-600">
                    Our expert teams execute the transformation plan, implementing new technologies and integrating them with your existing systems. We follow agile methodologies to deliver value quickly and adapt to changing requirements.
                  </p>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center md:justify-end">
                      <div className="w-5 h-5 flex items-center justify-center text-teal-600 mr-2 md:order-last md:ml-2 md:mr-0">
                        <i className="ri-check-line"></i>
                      </div>
                      <span className="text-gray-700">Agile implementation</span>
                    </div>
                    <div className="flex items-center md:justify-end">
                      <div className="w-5 h-5 flex items-center justify-center text-teal-600 mr-2 md:order-last md:ml-2 md:mr-0">
                        <i className="ri-check-line"></i>
                      </div>
                      <span className="text-gray-700">Systems integration</span>
                    </div>
                    <div className="flex items-center md:justify-end">
                      <div className="w-5 h-5 flex items-center justify-center text-teal-600 mr-2 md:order-last md:ml-2 md:mr-0">
                        <i className="ri-check-line"></i>
                      </div>
                      <span className="text-gray-700">Data migration</span>
                    </div>
                  </div>
                </div>
                <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center text-white text-xl font-bold z-10 mx-8">
                  3
                </div>
                <div className="md:w-1/2 md:pl-12">
                  <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                      <div className="w-7 h-7 flex items-center justify-center text-teal-600">
                        <i className="ri-code-box-line text-xl"></i>
                      </div>
                    </div>
                    <h4 className="font-semibold mb-2">Key Deliverables</h4>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Deployed digital solutions</li>
                      <li>• Integration with existing systems</li>
                      <li>• User training programs</li>
                      <li>• Testing and validation reports</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Phase 4 */}
              <div className="flex flex-col md:flex-row items-center">
                <div className="md:w-1/2 md:pr-12 mb-8 md:mb-0 order-last md:order-first">
                  <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                      <div className="w-7 h-7 flex items-center justify-center text-teal-600">
                        <i className="ri-line-chart-line text-xl"></i>
                      </div>
                    </div>
                    <h4 className="font-semibold mb-2">Key Deliverables</h4>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Performance dashboards</li>
                      <li>• ROI analysis reports</li>
                      <li>• Continuous improvement plans</li>
                      <li>• Knowledge transfer documentation</li>
                    </ul>
                  </div>
                </div>
                <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center text-white text-xl font-bold z-10 mx-8">
                  4
                </div>
                <div className="md:w-1/2 md:pl-12 md:text-left">
                  <span className="text-teal-600 font-semibold mb-2 block text-sm">PHASE 4</span>
                  <h3 className="text-2xl font-bold mb-3">Optimization & Evolution</h3>
                  <p className="text-gray-600">
                    Digital transformation is an ongoing journey. We continuously monitor performance, gather feedback, and optimize your digital solutions to ensure they deliver maximum value and adapt to evolving business needs.
                  </p>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center">
                      <div className="w-5 h-5 flex items-center justify-center text-teal-600 mr-2">
                        <i className="ri-check-line"></i>
                      </div>
                      <span className="text-gray-700">Performance monitoring</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-5 h-5 flex items-center justify-center text-teal-600 mr-2">
                        <i className="ri-check-line"></i>
                      </div>
                      <span className="text-gray-700">Continuous improvement</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-5 h-5 flex items-center justify-center text-teal-600 mr-2">
                        <i className="ri-check-line"></i>
                      </div>
                      <span className="text-gray-700">Innovation roadmapping</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">The Benefits of Digital Transformation</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Discover how digital transformation can drive significant improvements across your entire organization.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md text-center">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-teal-600">
                  <i className="ri-speed-line text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Increased Efficiency</h3>
              <p className="text-gray-600">
                Streamline operations, automate manual tasks, and optimize workflows to significantly improve productivity and reduce costs.
              </p>
              <div className="mt-4">
                <div className="flex items-center justify-center">
                  <span className="text-2xl font-bold text-teal-600">35%</span>
                  <span className="text-gray-500 ml-2 text-sm">Average productivity improvement</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md text-center">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-teal-600">
                  <i className="ri-user-smile-line text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Enhanced Customer Experience</h3>
              <p className="text-gray-600">
                Create seamless, personalized customer journeys across all touchpoints to increase satisfaction and build loyalty.
              </p>
              <div className="mt-4">
                <div className="flex items-center justify-center">
                  <span className="text-2xl font-bold text-teal-600">42%</span>
                  <span className="text-gray-500 ml-2 text-sm">Improvement in customer satisfaction</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md text-center">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-teal-600">
                  <i className="ri-line-chart-line text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Data-Driven Insights</h3>
              <p className="text-gray-600">
                Leverage advanced analytics to gain valuable business insights, identify trends, and make informed strategic decisions.
              </p>
              <div className="mt-4">
                <div className="flex items-center justify-center">
                  <span className="text-2xl font-bold text-teal-600">58%</span>
                  <span className="text-gray-500 ml-2 text-sm">More accurate business forecasting</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md text-center">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-teal-600">
                  <i className="ri-rocket-line text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Agility & Innovation</h3>
              <p className="text-gray-600">
                Build a flexible, adaptable organization that can quickly respond to market changes and drive continuous innovation.
              </p>
              <div className="mt-4">
                <div className="flex items-center justify-center">
                  <span className="text-2xl font-bold text-teal-600">63%</span>
                  <span className="text-gray-500 ml-2 text-sm">Faster time-to-market for new offerings</span>
                </div>
              </div>
            </div>
          </div>

          {/* ROI Section */}
          <div className="mt-16">
            <div className="bg-white rounded-lg p-8 border border-gray-200 shadow-sm">
              <div className="flex flex-col lg:flex-row items-center">
                <div className="w-full lg:w-1/2 mb-8 lg:mb-0 lg:pr-12">
                  <h3 className="text-2xl font-bold mb-4">ROI of Digital Transformation</h3>
                  <p className="text-gray-600 mb-6">
                    Our clients typically see significant returns on their digital transformation investments, with benefits extending across multiple business dimensions.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <div className="w-full bg-gray-200 rounded-full h-4 mr-4">
                        <div className="bg-teal-600 h-4 rounded-full" style={{ width: '78%' }}></div>
                      </div>
                      <span className="text-gray-700 font-medium whitespace-nowrap text-sm">Cost Reduction: 78%</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-full bg-gray-200 rounded-full h-4 mr-4">
                        <div className="bg-teal-600 h-4 rounded-full" style={{ width: '65%' }}></div>
                      </div>
                      <span className="text-gray-700 font-medium whitespace-nowrap text-sm">Revenue Growth: 65%</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-full bg-gray-200 rounded-full h-4 mr-4">
                        <div className="bg-teal-600 h-4 rounded-full" style={{ width: '82%' }}></div>
                      </div>
                      <span className="text-gray-700 font-medium whitespace-nowrap text-sm">Operational Efficiency: 82%</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-full bg-gray-200 rounded-full h-4 mr-4">
                        <div className="bg-teal-600 h-4 rounded-full" style={{ width: '71%' }}></div>
                      </div>
                      <span className="text-gray-700 font-medium whitespace-nowrap text-sm">Customer Retention: 71%</span>
                    </div>
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <div className="w-full h-80">
                    <img 
                      src="https://readdy.ai/api/search-image?query=colorful%20pie%20chart%20showing%20digital%20transformation%20ROI%20breakdown%20with%20four%20segments%20in%20teal%20blue%20orange%20and%20green%20colors%20on%20white%20background%20clean%20professional%20business%20analytics%20visualization&width=600&height=400&seq=dt002&orientation=landscape"
                      alt="ROI Chart"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
            <div className="flex flex-col md:flex-row">
              <div className="w-full md:w-2/5 bg-teal-600 p-8 text-white">
                <h2 className="text-2xl font-bold mb-6">Start Your Digital Transformation Journey</h2>
                <p className="mb-8">
                  Contact our team of experts to discuss how we can help your organization leverage digital technologies to achieve your business goals.
                </p>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <div className="w-5 h-5 flex items-center justify-center text-white">
                        <i className="ri-phone-line"></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Call Us</h3>
                      <p>+31 (0)6 1074 9063</p>
                      <p className="text-sm text-white/80">Monday-Friday, 9am-6pm EST</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <div className="w-5 h-5 flex items-center justify-center text-white">
                        <i className="ri-mail-line"></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Email Us</h3>
                      <p>sales@thyamis.com</p>
                      <p className="text-sm text-white/80">We'll respond within 24 hours</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <div className="w-5 h-5 flex items-center justify-center text-white">
                        <i className="ri-map-pin-line"></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Visit Us</h3>
                      <p>Energieweg 2</p>
                      <p className="text-sm text-white/80">3641RT Mijdrecht, Netherlands</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full md:w-3/5 p-8">
                <h3 className="text-xl font-semibold mb-6">Contact Us</h3>
                <form data-readdy-form id="contact-form" onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600/50 focus:border-teal-600 text-sm"
                        placeholder="John Smith"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600/50 focus:border-teal-600 text-sm"
                        placeholder="Your Company"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600/50 focus:border-teal-600 text-sm"
                        placeholder="john@example.com"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600/50 focus:border-teal-600 text-sm"
                        placeholder="(555) 123-4567"
                        required
                      />
                    </div>
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Interested Services
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="services"
                          value="Business Process Automation"
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-600"
                        />
                        <span className="ml-2 text-sm text-gray-700">Business Process Automation</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="services"
                          value="Cloud Migration"
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-600"
                        />
                        <span className="ml-2 text-sm text-gray-700">Cloud Migration</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="services"
                          value="Data Analytics & BI"
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-600"
                        />
                        <span className="ml-2 text-sm text-gray-700">Data Analytics & BI</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="services"
                          value="Digital Workplace Solutions"
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-600"
                        />
                        <span className="ml-2 text-sm text-gray-700">Digital Workplace Solutions</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="services"
                          value="IoT Integration"
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-600"
                        />
                        <span className="ml-2 text-sm text-gray-700">IoT Integration</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="services"
                          value="AI & Machine Learning"
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-600"
                        />
                        <span className="ml-2 text-sm text-gray-700">AI & Machine Learning</span>
                      </label>
                    </div>
                  </div>
                  <div className="mb-6">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Project Requirements
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600/50 focus:border-teal-600 text-sm"
                      placeholder="Tell us about your project and requirements..."
                      required
                    ></textarea>
                    <div className="text-sm text-gray-500 mt-1">
                      <span>{messageLength}</span>/500 characters
                    </div>
                  </div>
                  <div className="flex items-start mb-6">
                    <div className="flex items-center h-5">
                      <input
                        id="privacy"
                        name="privacy"
                        type="checkbox"
                        className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-teal-600"
                        required
                      />
                    </div>
                    <label htmlFor="privacy" className="ml-2 text-sm text-gray-600">
                      I agree to the <a href="#" className="text-teal-600 hover:underline">Privacy Policy</a> and consent to being contacted about Thyamis services.
                    </label>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-teal-600 text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium w-full hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feedback Modal */}
      {showFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            {feedbackType === 'success' ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-green-500 text-3xl"></i>
                </div>
                <h3 className="text-xl font-semibold mb-2">Thank You!</h3>
                <p className="text-gray-600">Your inquiry has been submitted successfully. We'll get back to you soon.</p>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-close-line text-red-500 text-3xl"></i>
                </div>
                <h3 className="text-xl font-semibold mb-2">Submission Failed</h3>
                <p className="text-gray-600">There was an error submitting your inquiry. Please try again later.</p>
              </div>
            )}
            <button
              onClick={closeFeedback}
              className="mt-6 bg-gray-100 text-gray-800 px-6 py-2 rounded-lg w-full hover:bg-gray-200 cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
